import React, { useState, useEffect } from 'react';
import { XIcon, CheckCircle2, Sparkles } from 'lucide-react';
import { usePremium } from './PremiumProvider';
import { EXCHANGE_RATE_API_KEY, PRICING_TIERS } from './constants';
import { lemonSqueezyService } from '../../services/lemonSqueezyService';
import { toast } from 'react-hot-toast';

const PremiumUpgradeModal = ({ isOpen, onClose }) => {
  const { userProfile } = usePremium();
  const [selectedTier, setSelectedTier] = useState('6 months');
  const [userCurrency, setUserCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(1);

  useEffect(() => {
    const testApiConnection = async () => {
      try {
        const products = await lemonSqueezyService.getProducts();
        
        // Get variants for the first product
        if (products.length > 0) {
          const variants = await lemonSqueezyService.getVariants(products[0].id);
          
          // Compare with our PRICING_TIERS
        }
      } catch (error) {
        console.error('❌ API test failed:', error);
        toast.error('API connection failed');
      }
    };

    if (isOpen) {
      testApiConnection();
    }
  }, [isOpen]);

  useEffect(() => {
    // No need to fetch user location or exchange rates
    setUserCurrency('USD');
    setExchangeRate(1); // Always use 1 as the exchange rate
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const handleChoosePlan = async () => {
    try {
      const selectedPlan = PRICING_TIERS.find(tier => tier.period === selectedTier);
      
      if (!selectedPlan) {
        console.error('❌ No plan selected');
        return;
      }

      if (!selectedPlan.productId) {
        throw new Error('Product ID is not defined for the selected plan');
      }

      const checkout = await lemonSqueezyService.createCheckout(
        selectedPlan.productId,
        userProfile.id
      );

      // Check if we have a URL before redirecting
      if (checkout?.attributes?.url) {
        window.location.href = checkout.attributes.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('❌ Checkout error:', error);
      toast.error('Failed to start checkout process. Please try again.');
    }
  };

  // Helper to format period text
  const formatPeriod = (period) => {
    return period.replace(/(^\w|\s\w)/g, letter => letter.toUpperCase());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8 flex items-start justify-center">
        <div className="bg-white rounded-xl w-full max-w-xl mx-auto shadow-xl border-2 border-neutral-900 relative">
          <div className="p-4 sm:p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 sm:mb-5">
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900">Upgrade to Premium</h2>
              <button
                onClick={onClose}
                className="text-neutral-500 hover:text-neutral-700 transition-colors"
                aria-label="Close modal"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Premium Features */}
            <div className="space-y-4 mb-5">
              <p className="text-sm sm:text-base font-semibold text-neutral-800">
                Upgrade today and unlock:
              </p>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Mastery Filters</span>
                    <span className="text-neutral-600 text-sm"> </span>
                    <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">HIGHLY RECOMMENDED</span>
                    <span className="text-neutral-600 text-sm"> – Focus only on baskets you need to review, so you don't waste time on what you already know</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Effortless Tracking</span>
                    <span className="text-neutral-600 text-sm"> – Save time monitoring volume reduction</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Progress Insights</span>
                    <span className="text-neutral-600 text-sm"> – Track when you are close to reducing your study volume by 40%!</span>
                  </div>
                </li>
                <li className="flex items-start gap-2 pt-2 border-t border-neutral-200">
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                    <span className="text-amber-500 font-bold text-sm">PLUS</span>
                    <Sparkles className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <span className="font-semibold">Exclusive Early Access</span>
                    <span className="text-neutral-600 text-sm"> – Be the first to try </span>
                    <span className="font-semibold">Weakest Links, Quiz Mode, and more!</span>
                    <div className="mt-0.5 text-xs text-amber-600 bg-amber-50 py-0.5 px-1.5 rounded-md inline-block">
                      Coming Soon
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            {/* Pricing Tiers */}
            <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-3">
              {PRICING_TIERS.map((tier) => (
                <div 
                  key={tier.period}
                  onClick={() => setSelectedTier(tier.period)}
                  className={`relative border-2 rounded-xl p-3 sm:p-4 transition-all cursor-pointer
                    ${selectedTier === tier.period 
                      ? 'border-cranberry-600 bg-cranberry-50/50' 
                      : 'border-neutral-200 hover:border-neutral-400'}`}
                >
                  <div className="text-center">
                    {tier.label && (
                      <div className="mb-1.5">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full
                          ${tier.period === '6 months' 
                            ? 'bg-cranberry-100 text-cranberry-700' 
                            : 'bg-emerald-100 text-emerald-700'}`}>
                          {tier.label}
                        </span>
                      </div>
                    )}
                    <h3 className="font-semibold text-base capitalize mb-1">{tier.period}</h3>
                    <div className="text-lg sm:text-xl font-bold text-cranberry-700 mb-0.5">
                      {formatPrice(tier.price)}
                    </div>
                    <div className="text-xs sm:text-sm text-neutral-600 mb-1.5">
                      {formatPrice(tier.perMonth)}/month
                    </div>
                    {tier.savings && (
                      <div className="text-xs font-medium text-emerald-600 bg-emerald-50 py-0.5 px-1.5 rounded-full inline-block">
                        Save {tier.savings}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Recommendations */}
            <div className="mb-4 p-2.5 sm:p-3 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="text-xs font-medium text-blue-700 mb-1">Recommendation</h4>
              {PRICING_TIERS.find(tier => tier.period === selectedTier)?.recommendation && (
                <p className="text-xs text-blue-600 leading-relaxed">
                  {PRICING_TIERS.find(tier => tier.period === selectedTier)?.recommendation}
                </p>
              )}
            </div>

            {/* Action Button */}
            <button
              onClick={handleChoosePlan}
              className="w-full py-2.5 px-4 bg-cranberry-700 text-white rounded-xl font-medium
                       hover:bg-cranberry-600 transition-colors duration-200
                       border-2 border-neutral-900 mb-3
                       text-sm sm:text-base"
            >
              Choose {formatPeriod(selectedTier)} Plan
            </button>

            {/* Disclaimer */}
            <p className="text-xs text-neutral-500 text-center px-2">
              * All prices are in USD.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal; 